import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  MenuItem,
  Divider,
  Grid,
} from "@mui/material";
import {
  getKycDetailsRequest,
  getAppConfigRequest,
} from "../../redux/Dashboard/dashboardSlice";
import {
  updateCustomerRequest,
  updateKycDetailsRequest,
} from "../../redux/Profile/profileSlice";
import { toast } from "react-toastify";
import { useAppTheme } from "../../theme/useAppTheme";

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(
    location.state?.tab === "bank" ? 2 : 0,
  );

   const customer = useSelector((state) => state.profile?.customer) || {};

  const { kycDetails, appConfig } = useSelector(
    (state) => state.dashboard || {},
  );

  const themeColors = useAppTheme();

  const [personalDetails, setPersonalDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dob: "",
    gender: "",
    state: "",
    pincode: "",
  });

  const [kycData, setKycData] = useState({
    panFirstName: "",
    panLastName: "",
    panNumber: "",
    status: "",
  });

  const [bankDetails, setBankDetails] = useState({
    accFirstName: "",
    accLastName: "",
    bankName: "",
    bankDropdown: "",
    ifsc: "",
    accNumber: "",
    status: "",
  });

  useEffect(() => {
    dispatch(getKycDetailsRequest());
    dispatch(getAppConfigRequest());
  }, [dispatch]);

  useEffect(() => {
    const dateOfBirth = customer.dateOfBirth;
    let dob = "";
    if (dateOfBirth) {
      const date = new Date(dateOfBirth);
      if (!Number.isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        dob = `${year}-${month}-${day}`;
      }
    }

    setPersonalDetails({
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      email: customer.email || "",
      phone: customer.phone || "",
      dob,
      gender: customer.gender || "",
      state: customer.address?.[0]?.state || "",
      pincode: customer.address?.[0]?.pincode || "",
    });
  }, [customer]);

  useEffect(() => {
    if (kycDetails) {
      const bank = kycDetails.bank_details || {};
      const pan = kycDetails.pan_details || {};

      setKycData({
        panFirstName: pan?.firstName || "",
        panLastName: pan?.lastName || "",
        panNumber: pan?.docno || "",
        status: pan?.verifyStatus || "",
      });

      setBankDetails({
        accFirstName: bank?.firstName || "",
        accLastName: bank?.lastName || "",
        bankName: bank?.bankName || "",
        bankDropdown: bank?.bankName || "",
        ifsc: bank?.ifsc || "",
        accNumber: bank?.accNumber || "",
        status: bank?.verifyStatus || "",
      });
    }
  }, [kycDetails]);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);
  const noLeadingSpaceFields = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "panFirstName",
    "panLastName",
    "panNumber",
    "accFirstName",
    "accLastName",
    "bankName",
    "bankDropdown",
    "ifsc",
    "accNumber",
  ];
  const handleChange = (setter) => (e) => {
    const { name, value } = e.target;
    const nextValue = name === "pincode"
      ? value.replace(/\D/g, "").slice(0, 13)
      : name === "ifsc"
        ? value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
        : noLeadingSpaceFields.includes(name)
          ? value.replace(/^\s+/, "")
          : value;
    setter((prev) => ({ ...prev, [name]: nextValue }));
  };

  const isBlank = (value) => String(value ?? "").trim() === "";

  const handleUpdate = () => {
    if (activeTab === 0) {
      const pincode = personalDetails.pincode || "";
      if (pincode && (pincode.length < 5 || pincode.length > 13)) {
        toast.error("Pin code must be between 5 and 13 digits.");
        return;
      }

      const payload = {
        address: [
          {
            area: "",
            city: "",
            pincode: personalDetails.pincode,
            state: personalDetails.state,
            addressType: customer.address?.[0]?.addressType || "",
          },
        ],
        firstName: personalDetails.firstName,
        lastName: personalDetails.lastName,
        gender: personalDetails.gender,
        dateOfBirth: new Date(personalDetails.dob).getTime(),
        ddmm: customer.ddmm || 0,
        referralcode: customer.referralcode || "",
        marritalStatus: customer.marritalStatus || "",
        email: personalDetails.email,
        phone: personalDetails.phone,
        deviceDetails: navigator.userAgent,
        jeweller: customer.jeweller || process.env.REACT_APP_JEWELLER_ID || "",
        refreshToken: localStorage.getItem("adminRefreshToken") || "",
        accessToken: localStorage.getItem("adminAccessToken") || "",
        _id: customer._id || "",
        customer_type: customer.customer_type || "",
        joining_code: customer.joining_code || "",
      };

      dispatch(updateCustomerRequest(payload));
    }

    if (activeTab === 1) {
      if (
        isBlank(kycData.panFirstName) ||
        isBlank(kycData.panLastName) ||
        isBlank(kycData.panNumber)
      ) {
        toast.error("All KYC fields are required.");
        return;
      }

      const payload = {
        type: "PAN",
        docName: "PAN",
        firstName: kycData.panFirstName,
        lastName: kycData.panLastName,
        docno: kycData.panNumber,
      };
      dispatch(updateKycDetailsRequest(payload));
    }

    if (activeTab === 2) {
      if (
        isBlank(bankDetails.accFirstName) ||
        isBlank(bankDetails.accLastName) ||
        isBlank(bankDetails.bankName) ||
        isBlank(bankDetails.bankDropdown) ||
        isBlank(bankDetails.ifsc) ||
        isBlank(bankDetails.accNumber)
      ) {
        toast.error("All bank fields are required.");
        return;
      }

      const payload = {
        type: "BANK",
        docName: "BANK",
        bankName: bankDetails.bankName,
        accNumber: bankDetails.accNumber,
        firstName: bankDetails.accFirstName,
        lastName: bankDetails.accLastName,
        ifsc: bankDetails.ifsc,
      };
      dispatch(updateKycDetailsRequest(payload));
    }
  };

  const banksList = appConfig?.bankList || [];

  const bankLabel = (bank) => {
    if (typeof bank === "string" || typeof bank === "number") return String(bank);
    if (bank && typeof bank === "object") {
      return String(bank.bankName || bank.name || bank.label || "");
    }
    return "";
  };

  const matchBankOption = (typed) => {
    const query = String(typed || "").trim().toLowerCase();
    if (!query) return "";

    const options = banksList
      .map((bank) => ({ value: bank, label: bankLabel(bank).trim() }))
      .filter((bank) => bank.label);
    const exact = options.find((bank) => bank.label.toLowerCase() === query);
    if (exact) return exact.label;
    const starts = options.find((bank) =>
      bank.label.toLowerCase().startsWith(query),
    );
    if (starts) return starts.label;
    const includes = options.find((bank) =>
      bank.label.toLowerCase().includes(query),
    );
    return includes ? includes.label : "";
  };

  const handleBankDetailsChange = (e) => {
    const { name, value } = e.target;
    const nextValue = String(value ?? "").replace(/^\s+/, "");

    if (name === "bankName") {
      setBankDetails((prev) => ({
        ...prev,
        bankName: nextValue,
        bankDropdown: matchBankOption(nextValue),
      }));
      return;
    }

    if (name === "bankDropdown") {
      setBankDetails((prev) => ({
        ...prev,
        bankDropdown: nextValue,
        bankName: bankLabel(nextValue).replace(/^\s+/, ""),
      }));
      return;
    }

    setBankDetails((prev) => ({ ...prev, [name]: nextValue }));
  };
  const statesList = [
    "N.A.",
    "Andaman and Nicobar Islands",
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chandigarh",
    "Chhattisgarh",
    "Dadra and Nagar Haveli",
    "Daman and Diu",
    "Delhi",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jammu and Kashmir",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Lakshadweep",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Puducherry",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  const textFieldSx = {
    "& .MuiInputBase-input": {
      color: themeColors.text, // typed text
    },
    "& .MuiInputLabel-root": {
      color: themeColors.text, // label
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: themeColors.primary, // focused label
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: themeColors.border,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: themeColors.primary,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: themeColors.primary,
    },
    "& .MuiOutlinedInput-root": {
      background: themeColors.mediumContainer,
      borderRadius: "12px",
    },
  };

  const renderField = (
    label,
    name,
    value,
    onChange,
    type = "text",
    options,
    required = false,
  ) => (
    <TextField
      key={name}
      select={!!options}
      fullWidth
      required={required}
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      type={type}
      size="small"
      margin="dense"
      sx={textFieldSx}
    >
      {options?.map((opt, idx) => {
        const optionText =
          typeof opt === "object" && opt !== null ? bankLabel(opt) : opt;
        return (
          <MenuItem key={idx} value={optionText}>
            {optionText}
          </MenuItem>
        );
      })}
    </TextField>
  );

  const getStatusStyle = (status = "") => {
    const normalized = status.toLowerCase();

    if (normalized === "approved") {
      return {
        color: "#2e7d32",
        bg: "#e8f5e9",
        border: "#2e7d32",
      };
    }

    if (normalized === "rejected") {
      return {
        color: "#d32f2f",
        bg: "#fdecea",
        border: "#d32f2f",
      };
    }

    return {
      color: "#ef6c00",
      bg: "#fff3e0",
      border: "#ef6c00",
    };
  };

  const StatusBadge = ({ status }) => {
    const style = getStatusStyle(status);

    return (
      <Box
        sx={{
          px: 2,
          py: 1,
          borderRadius: "20px",
          textAlign: "center",
          fontWeight: 600,
          fontSize: "0.85rem",
          textTransform: "capitalize",
          color: style.color,
          backgroundColor: style.bg,
          border: `1px solid ${style.border}`,
        }}
      >
        {status || "Pending"}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        background: themeColors.scaffoldGradientColors,
        pb: 2,
        overflowX: "hidden",
        width: "100%",
        maxWidth: 1350,
        mx: "auto",
        borderRadius: { xs: 0, md: "30px" },
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        p: { xs: 1.5, sm: 2 },
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 650,
          borderRadius: 3,
          boxShadow: 5,
          p: { xs: 2, sm: 3 },
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          mb={2}
          sx={{
            color: themeColors.primary,
          }}
        >
          Profile Settings
        </Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{
            mb: 2,
            borderRadius: 2,
            backgroundColor: "transparent",

            "& .MuiTab-root": {
              color: themeColors.text,
              textTransform: "none",
              fontWeight: 500,
              borderRadius: 2,
              mx: 0.5,
              minHeight: 40,
            },

            "& .Mui-selected": {
              backgroundColor: themeColors.primary,
              color: `${themeColors.text} !important`,
            },
          }}
        >
          <Tab label="Personal" />
          <Tab label="KYC" />
          <Tab label="Bank" />
        </Tabs>

        <Divider sx={{ mb: 2 }} />

        <CardContent sx={{ p: 0 }}>
          {activeTab === 0 && (
            <Grid container spacing={2}>
              {renderField(
                "First Name",
                "firstName",
                personalDetails.firstName,
                handleChange(setPersonalDetails),
              )}
              {renderField(
                "Last Name",
                "lastName",
                personalDetails.lastName,
                handleChange(setPersonalDetails),
              )}
              {renderField(
                "Email",
                "email",
                personalDetails.email,
                handleChange(setPersonalDetails),
              )}
              {renderField(
                "Phone",
                "phone",
                personalDetails.phone,
                handleChange(setPersonalDetails),
              )}
              {renderField(
                "Date of Birth",
                "dob",
                personalDetails.dob,
                handleChange(setPersonalDetails),
                "date",
              )}
              {renderField(
                "Gender",
                "gender",
                personalDetails.gender,
                handleChange(setPersonalDetails),
                "text",
                ["Male", "Female", "Other"],
              )}
              {renderField(
                "Pin Code",
                "pincode",
                personalDetails.pincode,
                handleChange(setPersonalDetails),
              )}
              {renderField(
                "State",
                "state",
                personalDetails.state,
                handleChange(setPersonalDetails),
                "text",
                statesList,
              )}
            </Grid>
          )}

          {activeTab === 1 && (
            <Grid container spacing={2}>
              {renderField(
                "PAN First Name",
                "panFirstName",
                kycData.panFirstName,
                handleChange(setKycData),
                "text",
                undefined,
                true,
              )}
              {renderField(
                "PAN Last Name",
                "panLastName",
                kycData.panLastName,
                handleChange(setKycData),
                "text",
                undefined,
                true,
              )}
              {renderField(
                "PAN Number",
                "panNumber",
                kycData.panNumber,
                handleChange(setKycData),
                "text",
                undefined,
                true,
              )}
              {/* {renderField("Status", "status", kycData.status, handleChange(setKycData))} */}
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    borderRadius: 2,
                    background: themeColors.mediumContainer,
                    border: `1px solid ${themeColors.scaffold}`,
                    color: themeColors.text,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      marginRight: "20px",
                    }}
                  >
                    PAN Verification Status
                  </Typography>

                  <StatusBadge status={kycData.status} />
                </Box>
              </Grid>
            </Grid>
          )}

          {activeTab === 2 && (
            <Grid container spacing={2}>
              {renderField(
                "Account Holder First Name",
                "accFirstName",
                bankDetails.accFirstName,
                handleChange(setBankDetails),
                "text",
                undefined,
                true,
              )}
              {renderField(
                "Account Holder Last Name",
                "accLastName",
                bankDetails.accLastName,
                handleChange(setBankDetails),
                "text",
                undefined,
                true,
              )}
              {renderField(
                "Bank Name",
                "bankName",
                bankDetails.bankName,
                handleBankDetailsChange,
                "text",
                undefined,
                true,
              )}
              {renderField(
                "Select Bank",
                "bankDropdown",
                bankDetails.bankDropdown,
                handleBankDetailsChange,
                "text",
                banksList.length > 0 ? banksList : [],
                true,
              )}
              {renderField(
                "IFSC",
                "ifsc",
                bankDetails.ifsc,
                handleChange(setBankDetails),
                "text",
                undefined,
                true,
              )}
              {renderField(
                "Account Number",
                "accNumber",
                bankDetails.accNumber,
                handleChange(setBankDetails),
                "text",
                undefined,
                true,
              )}
              {/* {renderField("Status", "status", bankDetails.status, handleChange(setBankDetails))} */}
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    borderRadius: 2,
                    background: themeColors.mediumContainer,
                    border: `1px solid ${themeColors.scaffold}`,
                    color: themeColors.text,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      marginRight: "20px",
                    }}
                  >
                    Bank Verification Status
                  </Typography>

                  <StatusBadge status={bankDetails.status} />
                </Box>
              </Grid>
            </Grid>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Button
              variant="contained"
              onClick={handleUpdate}
              sx={{
                borderRadius: 2,
                px: 6,
                py: 1.5,
                color: themeColors.text,
                background: themeColors.primary,
                border: `1px solid ${themeColors.scaffold}`,
              }}
            >
              Update
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Profile;

// Request /api/v1/kyc/savekycdetails body = {"type":"PAN","firstName":"one","lastName":"two","docno":"abcde1234","docName":"PAN"}
// Request /api/v1/kyc/savekycdetails body = {"type":"BANK","docName":"BANK","bankName":"HDFC BANK LTD","accNumber":"123458494948848","firstName":"one","lastName":"two","ifsc":"HDFC0000112"}
// Request /api/v1/customer/updateCustomer body = {"address":[{"area":"","city":"","pincode":"413110","state":"Maharashtra","addressType":""}],"firstName":"Zero","lastName":"Two","gender":"Male","dateOfBirth":-1078551000000,"ddmm":0,"referralcode":"","marritalStatus":"","email":"zero@gmail.com","phone":"9999900001","deviceDetails":"","jeweller":"6901b59b3a34b2a698c43092","refreshToken":"","accessToken":"","_id":"6901b9ab3a34b2a698c4390d","customer_type":"","joining_code":""}
